import { CircularProgress, Snackbar, TextField } from '@material-ui/core'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LaunchIcon from '@mui/icons-material/Launch'
import ButtonShadow from 'components/ButtonShadow/ButtonShadow'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { generateKycLink } from 'services/api'
import { Colors } from 'styles'
import openURL from 'utils/openURL'
import * as Styled from './RecipientKyc.styled'
import { Container } from './RecipientKyc.styled'
import { QRCodeSVG } from 'qrcode.react'

interface IError {
  message?: string
  tryAgainFunctionName?: string
}

interface RecipientProps {
  podcastId: string
}

const RecipientKyc: React.FC<RecipientProps> = ({ podcastId }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [kycUrl, setKycUrl] = useState<string | null>(null)
  const [kycAttempt, setKycAttempt] = useState<number | null>(null)
  const [error, setError] = useState<IError>({})
  const [copyTextResult, setCopyTextResult] = useState<string | undefined>(
    undefined,
  )
  const inputUrlRef = useRef<HTMLInputElement>(null)

  const fetchKycUrl = useCallback(async () => {
    setIsLoading(true)
    setError({})
    const kycLinkOrError = await generateKycLink(podcastId)

    if (kycLinkOrError.hasFailed()) {
      setIsLoading(false)

      setError({
        message: 'Falha ao continuar cadastro.',
        tryAgainFunctionName: 'fetchKycUrl',
      })
      return
    }

    setIsLoading(false)
    setKycUrl(kycLinkOrError.data.url)
    setKycAttempt(kycLinkOrError.data.attempt)
  }, [podcastId])

  useEffect(() => {
    fetchKycUrl()
  }, [fetchKycUrl])

  const handleCopyLink = useCallback(async () => {
    inputUrlRef.current?.select()
    const success = document.execCommand('copy')
    setCopyTextResult(success ? 'Link Copiado!' : 'Erro ao copiar link')
    inputUrlRef.current?.blur()
  }, [])

  const handleOpenLink = useCallback(async () => {
    await openURL(kycUrl)
  }, [kycUrl])

  return (
    <Container>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={!!copyTextResult}
        onClose={() => setCopyTextResult(undefined)}
        message={copyTextResult}
        autoHideDuration={3000}
      />

      {isLoading ? (
        <Styled.InfoContainer>
          <CircularProgress style={{ color: Colors.PURPLE[400] }} />
          <Styled.InfoText>Carregando...</Styled.InfoText>
        </Styled.InfoContainer>
      ) : error.message ? (
        <Styled.InfoContainer>
          <Styled.InfoText>{error.message}</Styled.InfoText>
          <ButtonShadow
            label="Tentar novamente"
            variant="secondary"
            onPress={
              error.tryAgainFunctionName === 'fetchKycUrl'
                ? fetchKycUrl
                : undefined
            }
          />
        </Styled.InfoContainer>
      ) : (
        <Styled.KycContainer>
          <Styled.KycInfoContainer>
            <Styled.KycInfoTitle>
              {kycAttempt > 1
                ? 'Por favor, refaça a verificação de identidade.'
                : 'Continue seu cadastro pelo celular usando o QRCode abaixo'}
            </Styled.KycInfoTitle>

            {kycAttempt > 1 && (
              <Styled.KycInfoDescription>
                A plataforma que cuida deste processo está exigindo uma nova
                tentativa.
                <br />
                Pedimos desculpas por isso.
                <br />
                Importante:{' '}
                <Styled.KycInfoBold>
                  confira todos os seus dados
                </Styled.KycInfoBold>{' '}
                e garanta uma{' '}
                <Styled.KycInfoBold>boa qualidade nas fotos</Styled.KycInfoBold>
              </Styled.KycInfoDescription>
            )}

            <Styled.KycInfoDescription>
              Você será redirecionado para a página da plataforma de pagamentos
              <br />
              Se preferir, utilize o link abaixo
              <br />
              Você irá precisar de uma webcam
            </Styled.KycInfoDescription>

            <Styled.KycInfoWarning>
              ❗ Preencha todos os dados com atenção e garanta que passou por
              todas as etapas do registro!
              <br />
              <br />⌛ Após finalizar o processo, aguarde nesta página para
              verificar o status do seu registro.
            </Styled.KycInfoWarning>
          </Styled.KycInfoContainer>

          <Styled.QrCodeContainer>
            <QRCodeSVG value={kycUrl} size={256} marginSize={4} />
          </Styled.QrCodeContainer>

          <Styled.LinkContainer>
            <TextField
              type="text"
              value={kycUrl}
              onChange={() => {}}
              variant="outlined"
              fullWidth
              inputRef={inputUrlRef}
            />

            <Styled.LinkActions>
              <ButtonShadow
                leftIcon={<ContentCopyIcon style={{ color: 'white' }} />}
                onPress={handleCopyLink}
              />
              <ButtonShadow
                leftIcon={<LaunchIcon style={{ color: 'white' }} />}
                onPress={handleOpenLink}
              />
            </Styled.LinkActions>
          </Styled.LinkContainer>
        </Styled.KycContainer>
      )}
    </Container>
  )
}

export default RecipientKyc
